// Cartões de "Minha conta" (Parte 3.8): perfil mínimo, configurações de como o portal reage ao usuário
// (Médico, Enfermeiro, Financeiro, Técnico em TI) e nível de vocabulário (Modo Simplificado vs. Modo Jargão Técnico),
// além de Modo Claro/Escuro, papéis (roles) e segundo fator (zero ícones decorativos).
import { Badge, Button } from '@giyus/ui';
import { useTranslation } from 'react-i18next';

import { useMe } from '../../../lib/auth/authContext';
import { THEMES, type Theme, useTheme } from '../../../lib/theme/theme';
import { PERSONA_LENS_META, type PersonaLens } from '../../../portal/experience/glossary';
import { usePortalExperience } from '../../../portal/experience/PortalExperienceContext';
import { UserKindLabel } from '../components/UserStatusBadge';

const KNOWN_METHODS = ['Totp', 'RecoveryCodes', 'SmsOtp'] as const;
type KnownMethod = (typeof KNOWN_METHODS)[number];

function isKnownMethod(value: string): value is KnownMethod {
  return (KNOWN_METHODS as readonly string[]).includes(value);
}

const SPECIALTY_VOCABULARY_EXAMPLES: Readonly<
  Record<
    PersonaLens,
    readonly {
      readonly module: string;
      readonly plainExplanation: string;
      readonly expertExplanation: string;
    }[]
  >
> = {
  clinical: [
    {
      module: 'Contratos & Estudos',
      plainExplanation:
        'Remuneração médica por visita do protocolo, exames realizados no paciente e atendimento de Eventos Adversos (SAE).',
      expertExplanation:
        'Auditoria clínica do Schedule of Assessments (SoA), elegibilidade ICH-GCP E6(R3), cobertura de Screen Failures (1:3) e reporte de SAE/SUSAR em 24h.',
    },
    {
      module: 'Compras (Fase 1 → 2)',
      plainExplanation:
        'Reposição de kits de coleta e materiais médicos antes do vencimento para nunca faltar no atendimento do paciente.',
      expertExplanation:
        'Priorização de fornecedores com SLA Fast-Track (<24h) para proteger janelas protocolares e evitar desvios por falta de insumo diagnóstico.',
    },
    {
      module: 'Coletas c/ Fornecedor',
      plainExplanation:
        'Retirada segura de amostras biológicas (sangue/biópsia) e calibração de equipamentos clínicos após autorização.',
      expertExplanation:
        'Rastreabilidade de cadeia fria (-80°C) IATA e bloqueio preventivo de agendamento caso equipamentos apresentem certificado RBC vencido.',
    },
    {
      module: 'Contabilidade & Notas',
      plainExplanation:
        'Acumulado das consultas e visitas médicas concluídas nos participantes para liberação do repasse financeiro.',
      expertExplanation:
        'Conciliação de honorários de investigação (PI/Sub-I) por marco clínico SoA validado no eCRF sem risco de prescrição contratual.',
    },
  ],
  operations: [
    {
      module: 'Contratos & Estudos',
      plainExplanation:
        'Tabela de procedimentos por visita (SoA), tempo de enfermagem/farmácia e exigências operacionais do protocolo.',
      expertExplanation:
        'Dimensionamento de hora-técnica (COREN/CRF/CRBM), processamento centrífugo refrigerado, dispensação IWRS/IP e SLA de transcrição em eCRF (5d).',
    },
    {
      module: 'Compras (Fase 1 → 2)',
      plainExplanation:
        'Reposição da farmácia/enfermaria para lotes a vencer (FEFO) e kits necessários para os pacientes agendados na semana.',
      expertExplanation:
        'Controle de lotes FEFO (First-Expired, First-Out) na bancada clínica com disparo automático por ponto de pedido e Lead Time de mobilização.',
    },
    {
      module: 'Coletas c/ Fornecedor',
      plainExplanation:
        'Você aprova a retirada e a transportadora agenda o horário de coleta (Seg–Qua com gelo seco) ou calibração.',
      expertExplanation:
        'Orquestração de embarque biológico IATA (Seg–Qua), manifesto AWB/MTR e anexação de certificados RBC/ISO 17025 na linha do tempo do ativo.',
    },
    {
      module: 'Contabilidade & Notas',
      plainExplanation:
        'Visitas preenchidas no eCRF e reembolsos de pacientes acumulados automaticamente sem perder o prazo de 90 dias.',
      expertExplanation:
        'Gatilhos de faturamento a partir do Source Data / eCRF verificado pelo CRA, prevenindo glosa retroativa por prescrição de 90 dias.',
    },
  ],
  financial: [
    {
      module: 'Contratos & Estudos',
      plainExplanation:
        'Tabela financeira do CTA, câmbio PTAX BCB, impostos (16,33%), margem líquida e gatilhos de faturamento.',
      expertExplanation:
        'Modelagem JEV com recomposição tributária Gross-Up (Lucro Real 14,25% / 16,33%), Overhead Institucional 30%, Holdback 10% e liquidação SWIFT CSIXKYKY.',
    },
    {
      module: 'Compras (Fase 1 → 2)',
      plainExplanation:
        'Mapa comparativo de 3 cotações (3-Bids), tempo de resposta do fornecedor, alçadas SoD e compra autônoma dentro do teto.',
      expertExplanation:
        'Adjudicação multicritério (TCO × Score de SLA de Resposta), validação de alçada SoD (2 pessoas) e trava contra estouro de teto Pass-Through.',
    },
    {
      module: 'Coletas c/ Fornecedor',
      plainExplanation:
        'Custo de frete cadeia fria, conferência de manifesto AWB/MTR + Certificado RBC e liberação 3-Way Match.',
      expertExplanation:
        'Conciliação 3-Way Match (Ordem de Compra ↔ Manifesto de Execução Clínica ↔ NF-e) antes do provisionamento no razão Contas a Pagar (AP).',
    },
    {
      module: 'Contabilidade & Notas',
      plainExplanation:
        'Acumulador por contrato/estudo, emissão de NFS-e / Commercial Invoice, Holdback 10% e retenções IRRF/CSRF 6,15%.',
      expertExplanation:
        'Apuração de margem realizada AP×AR com PTAX BCB congelada na liquidação, retenções na fonte (IRRF 1,5% + CSRF 4,65%) e resgate de Holdback no Close-Out.',
    },
  ],
  admin: [
    {
      module: 'Contratos & Estudos',
      plainExplanation:
        'Governança contratual CTA, cláusulas de indenização, guarda regulatória de 25 anos e deliberação colegiada Multi-LLM.',
      expertExplanation:
        'Gestão executiva de contratos clínicos (CTA), matrizes de visitas e procedimentos (JEV/SoA) e memória institucional auditável.',
    },
    {
      module: 'Módulo de Compras',
      plainExplanation:
        'Aprovação e acompanhamento de ordens de compra, cotações com fornecedores e conciliação de notas fiscais.',
      expertExplanation:
        'Segregação de funções (SoD), conciliação 3-Way Match e rastreabilidade completa de compras clínicas.',
    },
    {
      module: 'Coletas c/ Fornecedor',
      plainExplanation:
        'Orquestração B2B com isolamento por fornecedor, conformidade ANVISA/IATA/ISO 17025 e SLA de fornecedores homologados.',
      expertExplanation:
        'Controle de acesso restrito por fornecedor e integração com as caixas compartilhadas @giyus.com.br.',
    },
    {
      module: 'Contabilidade',
      plainExplanation:
        'Auditoria de receita clínica, trava contra prescrição de 90 dias, resgate de Holdback 10% e compliance fiscal.',
      expertExplanation:
        'Conciliação de faturamento com câmbio PTAX Banco Central, retenções fiscais e trilha de auditoria SHA-256.',
    },
  ],
  external: [
    {
      module: 'Contratos & Estudos',
      plainExplanation:
        'Acesso restrito apenas às demandas e protocolos vinculados à sua empresa parceira, laboratório ou monitoria.',
      expertExplanation:
        'Escopo B2B isolado sob Row-Level Security (RLS) para consulta de protocolos, grade SoA e cronogramas autorizados.',
    },
    {
      module: 'Compras (Fase 1 → 2)',
      plainExplanation:
        'Resposta a cotações abertas, confirmação de pedidos de compra (PO) e programação de entrega no centro.',
      expertExplanation:
        'Submissão de propostas comerciais em RFQs abertas com monitoramento de SLA de resposta (<24h Fast-Track) e confirmação de PO.',
    },
    {
      module: 'Coletas c/ Fornecedor',
      plainExplanation:
        'Painel para agendar data/hora de coleta aprovada pelo centro, informar motorista/AWB e enviar certificado RBC/CDR.',
      expertExplanation:
        'Registro de manifesto logístico IATA, upload de certificados de calibração RBC/ISO 17025 e confirmação de execução de OS.',
    },
    {
      module: 'Contabilidade & Notas',
      plainExplanation:
        'Acompanhamento de medições validadas, notas fiscais enviadas e data programada de pagamento.',
      expertExplanation:
        'Submissão de NF-e/NFS-e vinculada ao PO aprovado e rastreabilidade do status de conciliação 3-Way Match até a liquidação.',
    },
  ],
};

const THEME_LABELS: Readonly<Record<Theme, string>> = {
  light: 'Modo Claro (Alabaster Glass)',
  dark: 'Modo Escuro (Midnight Obsidian)',
  system: 'Automático do Sistema',
};

export function ProfileCard() {
  const { t } = useTranslation(['identity', 'common']);
  const me = useMe();
  const { theme, setTheme } = useTheme();
  const { personaLens, isPlainMode, setPersonaLens, setMode } = usePortalExperience();
  const activeLensMeta = PERSONA_LENS_META[personaLens];
  const examples = SPECIALTY_VOCABULARY_EXAMPLES[personaLens];

  const networkRoles = me.networkAccess.hasAccess ? me.networkAccess.roles : [];
  const tenantRoleSummaries = me.contexts.flatMap((ctx) =>
    ctx.effectiveRoles.map((r) => `${ctx.code}: ${r.role}`),
  );

  return (
    <section
      className="flex flex-col gap-4 rounded-card border border-border bg-surface p-5 shadow-raised"
      data-testid="account-profile"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-body font-bold text-heading">{t('identity:account.profile.title')}</h2>
        <span className="inline-flex items-center gap-2 rounded-card border border-brand bg-accent px-3 py-1 text-caption font-bold text-heading">
          <span>
            Perfil Ativo: {activeLensMeta.label} ·{' '}
            {isPlainMode ? 'Modo Simplificado' : 'Modo Jargão Técnico'}
          </span>
        </span>
      </div>

      <dl className="grid gap-2 text-body sm:grid-cols-[10rem_1fr]">
        <dt className="text-muted-foreground">{t('identity:account.profile.name')}</dt>
        <dd className="font-medium text-heading">{me.user.displayName}</dd>
        <dt className="text-muted-foreground">{t('identity:account.profile.email')}</dt>
        <dd className="font-mono text-caption text-heading">{me.user.email}</dd>
        <dt className="text-muted-foreground">{t('identity:account.profile.kind')}</dt>
        <dd>
          <UserKindLabel kind={me.user.kind} />
        </dd>
        <dt className="text-muted-foreground">{t('identity:account.profile.locale')}</dt>
        <dd>
          {me.user.preferredLocale === 'pt-BR' || me.user.preferredLocale === 'en-US'
            ? t(`common:language.${me.user.preferredLocale}`)
            : me.user.preferredLocale}
          <p className="text-caption text-muted-foreground">
            {t('identity:account.profile.localeHint')}
          </p>
        </dd>
        {me.person && (
          <>
            <dt className="text-muted-foreground">{t('identity:account.profile.personId')}</dt>
            <dd className="font-mono text-caption">{me.person.id}</dd>
          </>
        )}
      </dl>

      {/* Papéis Efetivos (Roles) & Escopo de Menu Autorizado */}
      <div className="rounded-card border border-border bg-surface p-3.5">
        <div className="text-caption font-bold tracking-wider text-heading uppercase">
          Papéis (Roles) Ativos &amp; Orientação de Menu / Ações
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {networkRoles.map((role) => (
            <span
              key={`net-${role}`}
              className="rounded-card border border-brand bg-accent px-2.5 py-0.5 font-mono text-caption font-bold text-heading"
            >
              Rede: {role}
            </span>
          ))}
          {tenantRoleSummaries.map((roleLabel) => (
            <span
              key={roleLabel}
              className="rounded-card border border-border-control bg-surface px-2.5 py-0.5 font-mono text-caption font-semibold text-heading"
            >
              Centro {roleLabel}
            </span>
          ))}
          {networkRoles.length === 0 && tenantRoleSummaries.length === 0 && (
            <span className="text-caption text-muted-foreground">
              Acesso mínimo de conta (/me) — sem papéis operacionais atribuídos.
            </span>
          )}
        </div>
      </div>

      {/* Aparência do Portal (Modo Claro e Modo Escuro) */}
      <div
        className="rounded-card border border-border bg-surface p-4"
        data-testid="account-theme-preferences-card"
      >
        <div className="text-caption font-bold tracking-wider text-heading uppercase">
          Aparência &amp; Contraste Visual (Modo Claro / Modo Escuro)
        </div>
        <p className="mt-0.5 text-caption text-foreground-secondary">
          Escolha o tema de iluminação do portal. Todos os painéis, tabelas e indicadores foram
          calibrados para leitura de alta precisão no modo claro e no modo escuro:
        </p>
        <div className="mt-2.5 grid gap-2 sm:grid-cols-3">
          {THEMES.map((item: Theme) => {
            const active = theme === item;
            return (
              <Button
                variant="ghost"
                key={item}
                type="button"
                onClick={() => {
                  setTheme(item);
                }}
                data-testid={`account-theme-${item}`}
                className={`flex items-center justify-between rounded-card border px-3.5 py-2.5 text-left text-caption font-bold transition ${
                  active
                    ? 'border-primary bg-surface text-heading shadow-2xs ring-2 ring-brand '
                    : 'border-border bg-surface-sunken text-foreground-secondary hover:bg-surface '
                }`}
              >
                <span>{THEME_LABELS[item]}</span>
                {active && (
                  <span className="rounded bg-primary px-1.5 py-0.5 font-mono text-caption text-primary-foreground">
                    ATIVO
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Configuração de Como o Site Reage à Pessoa (Médico, Enfermeiro, Financeiro, Técnico em TI) + Modo Simplificado vs. Jargão Técnico */}
      <div
        className="rounded-card border-2 border-brand bg-accent p-4"
        data-testid="account-profile-specialty-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <div className="text-caption font-bold tracking-wider text-link uppercase">
              Configuração do Usuário: Tipo de Pessoa &amp; Modo Simplificado vs. Jargão Técnico
            </div>
            <h3 className="mt-1 text-body font-bold text-heading">
              Ajuste como os textos de Estudos, Contratos, Compras e Equipamentos mudam conforme
              quem está acessando (Médico, Enfermeiro, Financeiro ou Técnico em TI)
            </h3>
            <p className="mt-0.5 text-caption text-foreground-secondary">
              Você pode combinar qualquer perfil profissional (<strong>Médico</strong>,{' '}
              <strong>Enfermeiro</strong>, <strong>Financeiro</strong> ou{' '}
              <strong>Técnico em TI</strong>) com o <strong>Modo Simplificado</strong> (linguagem
              direta) ou com o <strong>Modo Jargão Técnico</strong> (terminologia especializada da
              área).
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-caption font-bold tracking-wider text-muted-foreground uppercase">
              Nível de Vocabulário
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setMode('plain');
                }}
                data-testid="profile-mode-plain-btn"
                className={`rounded-card px-3 py-1.5 text-caption font-bold transition ${
                  isPlainMode
                    ? 'bg-primary text-primary-foreground '
                    : 'border border-border-control bg-surface text-foreground-secondary '
                }`}
              >
                Modo Simplificado
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  setMode('expert');
                }}
                data-testid="profile-mode-expert-btn"
                className={`rounded-card px-3 py-1.5 text-caption font-bold transition ${
                  !isPlainMode
                    ? 'bg-link text-primary-foreground '
                    : 'border border-border-control bg-surface text-foreground-secondary '
                }`}
              >
                Modo Jargão Técnico
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {(['clinical', 'operations', 'financial', 'admin', 'external'] as const).map((lens) => {
            const meta = PERSONA_LENS_META[lens];
            const isSelected = personaLens === lens;
            return (
              <Button
                variant="ghost"
                key={lens}
                type="button"
                onClick={() => {
                  setPersonaLens(lens);
                }}
                data-testid={`profile-specialty-${lens}`}
                className={`flex flex-col items-start rounded-card border p-3 text-left transition ${
                  isSelected
                    ? 'border-primary bg-surface shadow-raised ring-2 ring-brand '
                    : 'border-border bg-surface-sunken hover:bg-surface '
                }`}
              >
                <div className="flex w-full items-center justify-between gap-1">
                  <span className="font-mono text-caption font-bold tracking-wider text-link uppercase">
                    {meta.codeTag}
                  </span>
                  {isSelected && (
                    <span className="rounded bg-primary px-1.5 py-0.5 font-mono text-caption font-bold text-primary-foreground">
                      ATIVO
                    </span>
                  )}
                </div>
                <div className="mt-1 text-caption font-bold text-heading">{meta.shortLabel}</div>
                <p className="mt-1 text-caption leading-snug text-foreground-secondary">
                  {meta.description}
                </p>
              </Button>
            );
          })}
        </div>

        <div className="mt-3 rounded-card border border-border bg-surface p-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-caption font-bold text-heading">
              Como o texto muda para {activeLensMeta.label} no{' '}
              {isPlainMode ? 'Modo Simplificado' : 'Modo Jargão Técnico'}:
            </span>
            <span className="rounded bg-surface-sunken px-2 py-0.5 font-mono text-caption font-bold text-foreground-secondary">
              {activeLensMeta.codeTag} · {isPlainMode ? 'SIMPLIFICADO' : 'JARGÃO TÉCNICO'}
            </span>
          </div>
          <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {examples.map((item) => (
              <div
                key={item.module}
                className="rounded-card border-l-4 border-brand bg-surface p-2.5"
              >
                <div className="text-caption font-bold text-heading">{item.module}</div>
                <p className="mt-1 text-caption font-medium leading-snug text-heading">
                  <strong className="text-link">
                    {isPlainMode ? '[Simplificado] ' : '[Jargão Técnico] '}
                  </strong>
                  {isPlainMode ? item.plainExplanation : item.expertExplanation}
                </p>
                <p className="mt-1 border-t border-border pt-1 text-caption leading-snug text-muted-foreground">
                  <strong>{isPlainMode ? 'Em Jargão Técnico: ' : 'Em Modo Simplificado: '}</strong>
                  {isPlainMode ? item.expertExplanation : item.plainExplanation}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function MfaCard() {
  const { t } = useTranslation('identity');
  const me = useMe();
  return (
    <section
      className="flex flex-col gap-3 rounded-card border border-border bg-surface p-5 shadow-raised"
      data-testid="account-mfa"
    >
      <h2 className="text-body font-semibold text-heading">{t('account.mfa.title')}</h2>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={me.user.mfa.enrolled ? 'default' : 'destructive'}>
          {me.user.mfa.enrolled ? t('account.mfa.enrolled') : t('account.mfa.notEnrolled')}
        </Badge>
        {me.user.mfa.methods.map((method) => (
          <Badge key={method} variant="outline">
            {isKnownMethod(method) ? t(`account.mfa.methods.${method}`) : method}
          </Badge>
        ))}
      </div>
      <p className="text-caption text-muted-foreground">{t('account.mfa.resetHint')}</p>
    </section>
  );
}
